<template>
  <!-- Sidebar -->
  <aside
    class="fixed md:static inset-y-0 left-0 bg-white shadow-lg z-30 md:z-auto w-64 transform transition-transform duration-300"
    :class="isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'"
  >
    <div class="flex flex-col h-full">
      <!-- Logo -->
      <div class="flex items-center justify-between h-14 border-b px-4">
        <div class="flex items-center space-x-2">
          <img src="@/assets/itemtap.jpeg" alt="ItemTap Logo" class="h-10 w-10 rounded-full" />
          <span class="text-xl font-bold text-[#4d148c]">ItemTap</span>
        </div>

        <!-- Close button for mobile -->
        <button
          @click="$emit('close')"
          class="md:hidden text-gray-500 hover:text-red-600 text-xl"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto">
        <router-link
          v-for="item in navigationItems"
          :key="item.path"
          :to="item.path"
          @click="handleNavigation"  
          class="flex items-center px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-[#4d148c] hover:text-white transition duration-300 transform hover:scale-102"
          :class="{ 'bg-[#4d148c] text-white': $route.path === item.path }"
        >
          <i :class="['w-4 h-4 mr-2', item.icon]"></i>
          <span>{{ item.name }}</span>
        </router-link>
      </nav>

      <!-- Profile Section -->
      <div class="p-3 border-t">
        <div class="relative">
          <!-- Profile header -->
          <div
            class="flex items-center gap-3 cursor-pointer transform hover:scale-105 transition-transform duration-300"
            @click="showDropdown = !showDropdown"
          >
            <div
              class="w-9 h-9 rounded-full bg-[#4d148c] flex items-center justify-center text-white font-semibold shadow-md"
            >
              {{ userInitials }}
            </div>
            <div class="flex flex-col">
              <p class="text-sm font-medium text-gray-800">{{ userName }}</p>
              <span class="text-xs text-gray-500">View Options</span>
            </div>
            <i class="fas fa-chevron-down ml-auto text-gray-400"></i>
          </div>

          <!-- Dropdown Menu -->
          <div
            v-if="showDropdown"
            class="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-md shadow-lg transition-all duration-200 z-10"
          >
            <!-- Profile Settings -->
            <router-link
              to="/profile"
              @click="handleNavigation"
              class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#4d148c] transition"
            >
              <i class="fas fa-user-edit mr-2"></i> Profile Settings
            </router-link>

            <!-- Divider -->
            <div class="border-t my-1"></div>

            <!-- Logout -->
            <button
              @click="handleLogout"
              class="w-full text-left flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition"
            >
              <i class="fas fa-sign-out-alt mr-2"></i> Logout
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Profile Modal -->
    <ProfileUpdateModal
      :show="showProfileModal"
      @close="showProfileModal = false"
      @profile-updated="handleProfileUpdate"
    />
  </aside>
</template>

<script>
import ProfileUpdateModal from './ProfileUpdateModal.vue';
import api from '@/services/api';

export default {
  name: 'SidebarNavigation',
  props: {
    isOpen: { type: Boolean, default: true }
  },
  components: { ProfileUpdateModal },
  data() {
    return {
      navigationItems: [
        { name: 'Dashboard', path: '/dashboard', icon: 'fas fa-chart-line' },
        { name: 'All Items', path: '/items', icon: 'fas fa-list' },
        { name: 'Post New Item', path: '/post-item', icon: 'fas fa-plus-circle' }
      ],
      userName: '',
      userInitials: '',
      showProfileModal: false,
      showDropdown: false
    };
  },
  created() {
    this.setUserInfo();
  },
  methods: {
    setUserInfo() {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user?.name) {
        this.userName = user.name;
        this.userInitials = user.name
          .split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase();
      }
    },
    handleNavigation() {
      this.showDropdown = false;
      this.$emit('close');
    },
    async handleLogout() {
      this.showDropdown = false;
      try {
        await api.post('/api/logout');
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('tokenExpiry');
        this.$router.push('/login');
        this.$emit('close');
      } catch (error) {
        console.error('Logout error:', error);
      }
    },
    handleProfileUpdate(userData) {
      this.userName = userData.name;
      this.userInitials = userData.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase();
      const user = JSON.parse(localStorage.getItem('user'));
      user.name = userData.name;
      localStorage.setItem('user', JSON.stringify(user));
    }
  }
};
</script>

<style scoped>
/* smooth mobile animation */
@media (max-width: 768px) {
  aside {
    width: 16rem;
  }
}
</style>

<style scoped>
.animate-slideInLeft {
  animation: slideInLeft 0.6s ease-out forwards;
}

.animate-fadeIn {
  animation: fadeIn 0.6s ease-out forwards;
}

.animate-fadeInUp {
  animation: fadeInUp 0.6s ease-out forwards;
}

.animate-slideInUp {
  animation: slideInUp 0.6s ease-out forwards !important;
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-100px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
